// we handle these explicitly
const IGNORED_FIELDS = ['giving_frequency'];

interface InputProps extends React.HTMLProps<HTMLInputElement | HTMLSelectElement> {
    class: string;
    'data-options'?: string;
    'data-multiplier'?: string;
}

interface FormFieldConfig {
    selectOptions?: string[],
    givingMultiplier?: number,
};

export interface FormField {
    name: string,
    type: string,
    config: FormFieldConfig,
    fieldInputProps: React.HTMLProps<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
}

export type InputValue = string | number | readonly string[] | undefined;

const parseInputProps = (attributes: NamedNodeMap): InputProps => {
    const inputProps: InputProps = Array.from(attributes).reduce((acc, curr) => ({ ...acc, [curr.name]: curr.value }), { class: '' });

    return inputProps;
}

const parseFormField = (field: HTMLElement): FormField => {
    const name = field.querySelector('label')!.innerHTML;
    const fieldInput = field.querySelector('input, select, textarea')!;
    const { class: type, style: _, ...fieldInputProps } = parseInputProps(fieldInput.attributes);

    return {
        name,
        type,
        config: {
            selectOptions: type === 'select' ? fieldInputProps['data-options']?.split(';') : undefined,
            givingMultiplier: name === 'Amount' ? Number(fieldInputProps['data-multiplier']) : undefined,
        },
        fieldInputProps,
    };
};

export const parseFormFields = (fields: NodeList): { [key in string]: FormField } => {
    const parsedFields: { [key in string]: FormField } = {};

    fields.forEach((field) => {
        const fieldElement = field as HTMLElement;
        if (IGNORED_FIELDS.includes(fieldElement.title)) {
            return;
        }

        const parsedField = parseFormField(fieldElement);
        parsedFields[parsedField.fieldInputProps.name!] = parsedField;
    });

    return parsedFields;
};

export enum ActionType {
    CreditCard = 1,
    Pledge,
    PayPal,
    AppleGooglePay,
};

export interface ButtonAction {
    style: React.CSSProperties;
    text: string;
}

export const parseDonateActions = (actions: NodeList) => {
    const parsedActions: Partial<Record<ActionType, ButtonAction>> = {};

    actions.forEach((action) => {
        const actionElement = action as HTMLElement;
        const { color, backgroundColor } = actionElement.style;

        const dataType = actionElement.getAttribute('data-onclick');
        let type: ActionType | null = null;

        if (dataType === '1') type = ActionType.CreditCard;
        else if (dataType === '3') type = ActionType.Pledge;

        if (type === null) {
            // this means that we've detected an old paypal button on this page
            // skip it
            return;
        }

        parsedActions[type] = {
            style: {
                color,
                backgroundColor,
            },
            text: actionElement.innerText,
        };
    });

    return parsedActions;
}

const emailRegex = /.*@.*\..*/;
export const isEmail = (email: string) => emailRegex.test(email) && !email.endsWith('.');

interface GivingChoiceFrequency {
    name: string;
    value: string;
    selected: boolean;
}

export const parseGivingFrequencyChoices = (gfChoices: NodeList): GivingChoiceFrequency[] => {
    const choices: GivingChoiceFrequency[] = [];

    gfChoices.forEach((gfChoice) => {
        const label = gfChoice.childNodes.item(0) as HTMLElement;
        const inputElement = gfChoice.childNodes.item(1) as HTMLInputElement;

        choices.push({
            selected: label.className === 'selected',
            name: label.innerHTML,
            value: inputElement.value,
        });
    });

    return choices;
}

export const getPrefillFormValues = (prefillValues: URLSearchParams, form: { [key in string]: FormField }) => {
    const parsedValues: { [key in string]: string | number } = {};

    prefillValues.forEach((value, key) => parsedValues[key] = value);

    return parsedValues;
}

export const getIframeLegacyUrl = (legacyUrl: string) => {
    if (legacyUrl.startsWith('portal.test')) {
        return 'https://test.giveeasy.org';
    }

    if (legacyUrl.startsWith('portal.')) {
        return 'https://giveeasy.org';
    }

    return legacyUrl;
}

export const postGivingToTags = async (fields: { [key in string]: InputValue }, charityKey: string): Promise<string> => {
    const res = await fetch(`/odata/v1.0/records?charityKey=${charityKey}`, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(fields),
    });

    const result = await res.json();

    return result.compounduuid;
}
